import React, { useState, useEffect } from "react";
import { useCloud } from "../../hooks/useCloud";
import { toast } from "react-toastify";
import {
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus,
} from "lucide-react";
import DateFilter from "../DateFilter";
import dateStore from "../../hooks/useDate";

const REPORT_TYPES = [
  {
    id: "item-wise",
    name: "Item Wise Report",
    description: "Detailed report of all items sold",
  },
  {
    id: "cancelled-bill-reports",
    name: "Cancelled Bill Report",
    description: "Report of all cancelled bills",
  },
  {
    id: "cancelled-bill-item-reports",
    name: "Cancelled Bill Items Report",
    description: "Detailed report of items in cancelled bills",
  },
  {
    id: "order-type-revenue",
    name: "Order Type Revenue Report",
    description: "Revenue breakdown by order type",
  },
  {
    id: "revenue-report",
    name: "Revenue Report",
    description: "Complete revenue analysis report",
  },
];

const DownloadCenter = () => {
  const { downloadReport, createDownloadRequest } = useCloud();
  const { startDate, endDate, dateType } = dateStore;
  const [reports, setReports] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchReports = async () => {
    setRefreshing(true);
    try {
      const response = await downloadReport();
      if (response.status === "success") {
        setReports(response.data);
      } else {
        toast.error(response.message || "Failed to fetch reports");
      }
    } catch (err) {
      toast.error("An error occurred while fetching reports");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleRequestDownload = async () => {
    if (!selectedReport) {
      toast.error("Please select a report type first");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await createDownloadRequest({
        reportType: selectedReport,
        dateType: dateType,
        startDate: startDate,
        endDate: endDate,
      });

      if (response.status === "success") {
        toast.success("Download request created successfully");
        setIsModalOpen(false);
        fetchReports();
      } else {
        toast.error(response.message || "Failed to create download request");
      }
    } catch (error) {
      toast.error("An error occurred while creating download request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "download-center-status--completed";
      case "failed":
        return "download-center-status--failed";
      default:
        return "download-center-status--pending";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "failed":
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-600" />;
    }
  };

  const handleDownload = (filePath) => {
    if (!filePath) {
      toast.error("Download link not available");
      return;
    }
    window.open(filePath, "_blank");
  };

  return (
    <div className="download-center">
      {/* Header */}
      <div className="download-center-header">
        <h2 className="download-center-title">Download Center</h2>
        <div className="download-center-actions">
          <button
            onClick={() => setIsModalOpen(true)}
            className="download-center-new-button"
          >
            <Plus className="w-4 h-4" />
            New Download
          </button>
          <button
            onClick={fetchReports}
            disabled={refreshing}
            className="download-center-refresh-button"
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Download Request Modal */}
      {isModalOpen && (
        <div className="download-center-modal-overlay">
          <div className="download-center-modal">
            <div className="download-center-modal-header">
              <h3 className="download-center-modal-title">
                Request New Report Download
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="download-center-modal-close"
              >
                ×
              </button>
            </div>

            <div className="download-center-modal-section">
              <label className="download-center-modal-label">
                Select Report Type
              </label>
              <div className="download-center-report-types">
                {REPORT_TYPES.map((report) => (
                  <label
                    key={report.id}
                    className={`download-center-report-option ${
                      selectedReport === report.id
                        ? "download-center-report-option--selected"
                        : "download-center-report-option--unselected"
                    }`}
                  >
                    <input
                      type="radio"
                      name="report-type"
                      value={report.id}
                      className="download-center-report-radio"
                      checked={selectedReport === report.id}
                      onChange={(e) => setSelectedReport(e.target.value)}
                    />
                    <div className="download-center-report-info">
                      <p className="download-center-report-name">
                        {report.name}
                      </p>
                      <p className="download-center-report-description">
                        {report.description}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="download-center-modal-section">
              <label className="download-center-modal-label">
                Select Date Range
              </label>
              <DateFilter />
            </div>

            <div className="download-center-modal-footer">
              <button
                onClick={() => setIsModalOpen(false)}
                className="download-center-modal-button download-center-modal-button--cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestDownload}
                disabled={!selectedReport}
                className="download-center-modal-button download-center-modal-button--submit"
              >
                {isSubmitting ? "Requesting..." : "Request Download"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="download-center-stats">
        <div className="download-center-stat-card">
          <p className="download-center-stat-label">Total Reports</p>
          <p className="download-center-stat-value download-center-stat-value--total">
            {reports.length}
          </p>
        </div>
        <div className="download-center-stat-card">
          <p className="download-center-stat-label">Completed</p>
          <p className="download-center-stat-value download-center-stat-value--completed">
            {reports.filter((report) => report.status === "completed").length}
          </p>
        </div>
        <div className="download-center-stat-card">
          <p className="download-center-stat-label">Failed</p>
          <p className="download-center-stat-value download-center-stat-value--failed">
            {reports.filter((report) => report.status === "failed").length}
          </p>
        </div>
      </div>

      {/* Reports List */}
      <div className="download-center-table-container">
        <div className="download-center-table-wrapper">
          <table className="download-center-table">
            <thead className="download-center-table-header">
              <tr>
                <th className="download-center-table-header-cell">
                  Report Name
                </th>
                <th className="download-center-table-header-cell">Status</th>
                <th className="download-center-table-header-cell">Report ID</th>
                <th className="download-center-table-header-cell download-center-table-header-cell--right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="download-center-table-body">
              {reports.map((report) => (
                <tr key={report.reportId} className="download-center-table-row">
                  <td className="download-center-table-cell download-center-table-cell--name">
                    {report.fileName}
                  </td>
                  <td className="download-center-table-cell download-center-table-cell--status">
                    <div
                      className={`download-center-status ${getStatusColor(
                        report.status
                      )}`}
                    >
                      {getStatusIcon(report.status)}
                      <span className="capitalize">{report.status}</span>
                    </div>
                  </td>
                  <td className="download-center-table-cell download-center-table-cell--id">
                    {report.reportId}
                  </td>
                  <td className="download-center-table-cell download-center-table-cell--action">
                    {report.status === "completed" && (
                      <button
                        onClick={() => handleDownload(report.filePath)}
                        className="download-center-download-button"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    )}
                    {report.status === "failed" && (
                      <span className="download-center-failed-text">
                        Download failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {reports.length === 0 && (
          <div className="download-center-empty">
            <p className="download-center-empty-text">No reports available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadCenter;
